import React from 'react'
import NextLink from 'next/link'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import CircularProgressV2 from 'app/KinerjaPSPage/components/CircularProgressV2'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { useSummary } from 'app/KinerjaTimPenilaiPenilikPage/hooks/useSummary'
import useBasePath from 'app/KinerjaTimPenilaiPenilikPage/hooks/useBasePath'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePenilikTrackEvent } from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikTrackEvent'

type MenuCardProps = {
  isLocked?: boolean
  title: string
  subtitle?: string
  href?: string
  progress?: {
    value: number
    maxValue: number
  }
}
const MenuCard: React.FC<MenuCardProps> = ({
  isLocked = false,
  title,
  subtitle,
  href,
  progress,
}) => {
  return (
    <div className="flex items-center justify-between gap-6 p-6 bg-white rounded-md shadow-md">
      <div className="w-[84px] h-[84px] flex-none">
        <CircularProgressV2
          value={progress?.value ?? 0}
          maxValue={progress?.maxValue ?? 0}
          isLocked={isLocked}
          textVariant="heading-sm"
        />
      </div>
      <div className="col-span-10 space-y-1 grow">
        <div>
          <Text variant="display-sm">{title}</Text>
        </div>
        {Boolean(subtitle) && (
          <Badge>
            <Text variant="body-sm-bold">{subtitle}</Text>
          </Badge>
        )}
      </div>
      {!isLocked && Boolean(href) && (
        <div className="flex-none">
          <NextLink href={href}>
            <Button as="a" size="lg" className="w-32">
              Cek
            </Button>
          </NextLink>
        </div>
      )}
    </div>
  )
}

function TimPenilaiPenilikIndexPage() {
  const toggle = useToggleSdkContext()
  const penilaianEnabled =
    toggle?.['kinerja-penilik-tim-penilai-pelaksanaan-enabled'] || false
  const penetapanEnabled =
    toggle?.['kinerja-penilik-tim-penilai-penetapan-enabled'] || false
  const { trackEventBase } = usePenilikTrackEvent()

  const { basePath, isPPKPenilikUrl: isPPKPenilik } = useBasePath()

  // Summary API
  const { apiStatus, summary } = useSummary()

  const showPerencanaanSubtitle =
    Boolean(summary?.perencanaan?.done && summary?.perencanaan?.total) &&
    summary?.perencanaan?.done < summary?.perencanaan?.total

  React.useEffect(() => {
    trackEventBase('beranda', 'view')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (apiStatus === 'loading') {
    return null
  }

  return (
    <div className="pl-64">
      <KinerjaSidebar
        sticky
        activeMenu={
          isPPKPenilik
            ? 'pejabat-penilai-kinerja-penilik'
            : 'tim-kinerja-penilik'
        }
      />
      <div className="w-full gap-4 p-6 space-y-4 xl:space-y-0 xl:flex">
        <div className="w-9/12 space-y-4">
          <MenuCard
            title="Perencanaan Kinerja"
            subtitle={
              showPerencanaanSubtitle
                ? `${summary?.perencanaan?.done ?? 0}/${
                    summary?.perencanaan?.total ?? 0
                  } Perencanaan sudah disetujui`
                : null
            }
            href={`${basePath}/perencanaan`}
            progress={{
              value: summary?.perencanaan?.done,
              maxValue: summary?.perencanaan?.total,
            }}
          />
          <MenuCard
            title={isPPKPenilik ? 'Penilaian Kinerja' : 'Pemantauan Kinerja'}
            isLocked={!penilaianEnabled || !summary?.penilaian?.total}
            href={`${basePath}/pelaksanaan/beranda`}
            progress={{
              value: summary?.penilaian?.done,
              maxValue: summary?.penilaian?.total,
            }}
          />
          <MenuCard
            title={
              isPPKPenilik
                ? 'Penetapan Predikat Kinerja'
                : 'Rekomendasi Predikat Kinerja'
            }
            isLocked={!penetapanEnabled || !summary?.penetapan?.total}
            href={`${basePath}/penetapan`}
            progress={{
              value: summary?.penetapan?.done,
              maxValue: summary?.penetapan?.total,
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default TimPenilaiPenilikIndexPage
